import { createHash } from "crypto";
import getSongDuplicateKey from "./getSongDuplicateKey";
import type { Song, SongId, SongIdMap, Songs, SortKey, UserId } from "./types";

type SearchAndSortSongsArg = {
  includeFriendsSongs?: boolean;
  limitToSongIds?: SongId[];
  searchKey?: string;
  songs: Songs;
  songIdMap: SongIdMap;
  sortKey?: SortKey;
  userId?: UserId;
};

type SearchAndSortSongsResult = {
  songsListNoDuplicates: Song[];
  songsListDuplicates: {
    [key: string]: {
      [key: string]: Song;
    };
  };
  totalDuration: number;
};

const cache: Record<string, SearchAndSortSongsResult> = {};
const LOG_TAG = "🔍🎵 searchAndSortSongs";

export default function searchAndSortSongs({
  includeFriendsSongs = false,
  limitToSongIds,
  searchKey,
  songs,
  songIdMap,
  sortKey = "title",
  userId = "",
}: SearchAndSortSongsArg): SearchAndSortSongsResult {
  // Prune the songs list to only include songs that are in the limitToSongIds list
  if (limitToSongIds) {
    includeFriendsSongs = true;
    const songsTmp: Record<SongId, Song> = {};
    for (const songId of limitToSongIds) {
      // We may have a old songId in the setList for a since-deleted song - don't include those
      if (songs[songId]) {
        songsTmp[songId] = songs[songId];
      }
    }
    songs = songsTmp;
  }

  const createCacheKey = () => {
    const songsCacheKey = createHash("md5")
      .update(JSON.stringify(songs))
      .digest("hex");
    const songIdMapCacheKey = createHash("md5")
      .update(JSON.stringify(songIdMap))
      .digest("hex");
    let cacheKeyString = `${searchKey}-${includeFriendsSongs}-${sortKey}-${songsCacheKey}-${songIdMapCacheKey}`;

    if (limitToSongIds) {
      const limitToSongIdsCacheKey = createHash("md5")
        .update(JSON.stringify(limitToSongIds))
        .digest("hex");
      cacheKeyString += `-${limitToSongIdsCacheKey}`;
    }

    if (sortKey === "recent") {
      const lastViewedSong = Object.values(songIdMap).reduce(
        (a, b) => (a.last_viewed_timestamp > b.last_viewed_timestamp ? a : b),
        { last_viewed_timestamp: 0 },
      );
      cacheKeyString = `${cacheKeyString}-${lastViewedSong.last_viewed_timestamp}`;
    }
    return createHash("md5").update(cacheKeyString).digest("hex");
  };
  const cacheKey = createCacheKey();

  if (cache[cacheKey]) {
    // console.debug(LOG_TAG, "result FROM CACHE", {
    //   songsLength: Object.keys(songs).length,
    //   songIdMapLength: Object.keys(songIdMap).length,
    //   includeFriendsSongs,
    //   limitToSongIds,
    //   searchKey,
    //   sortKey,
    //   userId,
    //   cacheKey,
    //   //songNames: cache[cacheKey].songsListNoDuplicates.map(song => song.title),
    // });
    return cache[cacheKey];
  }

  console.debug(LOG_TAG, "GO-TIME (not-cached)", {
    includeFriendsSongs,
    limitToSongIds,
    searchKey,
    sortKey,
    userId,
    //songs,
    songsLength: Object.keys(songs).length,
    songIdMapLength: Object.keys(songIdMap).length,
  });
  //console.time("searchAndSortSongs");

  let songsList = Object.values(songs);

  if (searchKey) {
    songsList = songsList.filter(({ title, artist }) => {
      if (title?.toLowerCase().includes(searchKey.toLowerCase())) return true;
      if (artist?.toLowerCase().includes(searchKey.toLowerCase())) return true;
      return false;
    });
  }

  if (includeFriendsSongs === false && userId !== null) {
    songsList = songsList.filter(
      ({ created_by_user_id }) => created_by_user_id === userId,
    );
  }

  const orderByRecent = (a: Song, b: Song): number => {
    if (!songIdMap[a.id]) {
      //console.warn("Key missing in songIdMap[a]", { id: a.id, song: a });
      return 1;
    }
    if (!songIdMap[b.id]) {
      //console.warn("Key missing in songIdMap[b]", { id: b.id, song: b });
      return -1;
    }
    if (
      songIdMap[a.id].last_viewed_timestamp >
      songIdMap[b.id].last_viewed_timestamp
    ) {
      return -1;
    }
    if (
      songIdMap[a.id].last_viewed_timestamp <
      songIdMap[b.id].last_viewed_timestamp
    ) {
      return 1;
    }

    if (a.updated_timestamp > b.updated_timestamp) {
      return -1;
    }
    if (a.updated_timestamp < b.updated_timestamp) {
      return 1;
    }

    if (a.created_timestamp > b.created_timestamp) {
      return -1;
    }
    if (a.created_timestamp < b.created_timestamp) {
      return 1;
    }
    return 0;
  };

  const orderByAlpha = (a: Song, b: Song): number => {
    // @ts-ignore
    const aValue = a[sortKey].toUpperCase();
    // @ts-ignore
    const bValue = b[sortKey].toUpperCase();
    if (aValue > bValue) {
      return 1;
    }
    if (aValue < bValue) {
      return -1;
    }
    if (a.updated_timestamp > b.updated_timestamp) {
      return 1;
    }
    if (a.updated_timestamp < b.updated_timestamp) {
      return -1;
    }
    if (a.created_timestamp > b.created_timestamp) {
      return 1;
    }
    if (a.created_timestamp < b.created_timestamp) {
      return -1;
    }
    return 0;
  };

  if (sortKey) {
    const orderByFn = sortKey === "recent" ? orderByRecent : orderByAlpha;
    songsList = songsList.sort(orderByFn);
  }

  const songsListMap: Record<string, Song> = {};
  const songsListDuplicates: Record<string, Record<string, Song>> = {};
  let songsListNoDuplicates: Song[] = [];
  for (const song of songsList) {
    const key = getSongDuplicateKey(song);
    if (!songsListMap[key]) {
      songsListMap[key] = song;
      // Preserve the order of `limitToSongIds`
      if (limitToSongIds) {
        const index = limitToSongIds.indexOf(song.id);
        songsListNoDuplicates[index] = song;
      } else {
        songsListNoDuplicates.push(song);
      }
    } else {
      //console.debug("got dupe w/", key, song.title, song.id);
      if (!songsListDuplicates[songsListMap[key].id]) {
        songsListDuplicates[songsListMap[key].id] = {};
      }
      songsListDuplicates[songsListMap[key].id][song.id] = song;
    }
  }

  // Remove potentially since-deleted if trying to preserve `limitToSongIds` order
  if (limitToSongIds) {
    songsListNoDuplicates = songsListNoDuplicates.filter(Boolean);
  }

  let totalDuration = 0;
  for (const song of songsListNoDuplicates) {
    //console.debug(LOG_TAG, { duration: song.duration, song });
    if (typeof song.duration !== "number") {
      //console.debug(LOG_TAG, "NAN!!", { duration: song.duration, song });
    }
    totalDuration += song.duration || 0;
  }
  //console.debug(LOG_TAG, { totalDuration });

  const result = { songsListNoDuplicates, songsListDuplicates, totalDuration };
  cache[cacheKey] = result;

  //console.timeEnd("searchAndSortSongs");
  return result;
}
